import React, { useEffect, useState, useRef } from "react";
import { uploadCSV } from "../services/importService";
import { useNavigate } from "react-router-dom";

function Import() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const [state, setState] = useState("");
  const statesList = ["CA", "WA", "NJ"];

  const token = localStorage.getItem("token");

  const [location, setLocation] = useState(
    localStorage.getItem("location") || "CA",
  );

  const [copiers, setCopiers] = useState({});
  const [printers, setPrinters] = useState({});

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/");
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!file) return setMessage("Please select a CSV file");
    if (!state) return setMessage("Please select a state");

    setLoading(true);
    setMessage("");

    try {
      await uploadCSV(file, state, token);
      setMessage("CSV uploaded successfully ✅");
      setFile(null);
      setState("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      loadData(location);
    } catch (err) {
      setMessage(err.message);
    } finally {
      setLoading(false);
    }
  };

  const groupByManufacturer = (data) => {
    return data.reduce((acc, item) => {
      const key = (item.manufacturer || "UNKNOWN").toUpperCase();
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});
  };

  const [dataLoading, setDataLoading] = useState(false);

  const loadData = async (selectedLocation = location) => {
    setDataLoading(true);

    const url = `https://app.equipmentrecovery.com/api/upload/getProduct?location=${selectedLocation}`;

    try {
      const res = await fetch(url);
      const data = await res.json();

      if (!data.success || !data.allRecord || data.allRecord.length === 0) {
        setCopiers({});
        setPrinters({});
        setMessage("No records found");
        return;
      }

      let copiersArr = [];
      let printersArr = [];

      const cleanNumber = (val) =>
        parseFloat((val || "0").toString().replace(/,/g, "")) || 0;

      data.allRecord.forEach((item) => {
        const row = {
          id: item.id || item._id || item.ID,
          sku: item.Item || "-",
          model: item.Model || "-",
          manufacturer: item.Manufacturer || "-",
          totalMeter: cleanNumber(item.Total_Meter),
          colorMeter: cleanNumber(item.Color_Meter),
          bwMeter: cleanNumber(item.BW_Meter),
          description: item.Equipment_Description || "-",
          serialNumber: item.Serial_Number || "-",
          passCopy: (item.MIssing_or_Damaged || "")
            .toLowerCase()
            .includes("passed")
            ? "Yes"
            : "No",
        };

        if (item.Class === "COPIER" || item.Class === "WIDE FORMAT") {
          copiersArr.push(row);
        } else if (item.Class === "PRINTERS") {
          printersArr.push(row);
        }
      });

      setCopiers(groupByManufacturer(copiersArr));
      setPrinters(groupByManufacturer(printersArr));
      setMessage("");
    } catch (err) {
      console.error(err);
      setMessage("Failed to load inventory ❌");
      setCopiers({});
      setPrinters({});
    } finally {
      setDataLoading(false); // 🔥 stop loader
    }
  };

  useEffect(() => {
    loadData(location);
  }, [location]);

  const deleteItem = async (id) => {
    try {
      const token = localStorage.getItem("token");

      const res = await fetch(
        `https://app.equipmentrecovery.com/api/upload/deleteProduct/${id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await res.json();

      if (!res.ok) {
        console.error("Delete error:", data);
        throw new Error(data.message || "Delete failed");
      }

      return data;
    } catch (err) {
      console.error("Delete exception:", err);
      throw err;
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;

    try {
      await deleteItem(id);

      // 🔥 reload data after delete
      loadData(location);

      setMessage("Deleted successfully ✅");
    } catch (err) {
      setMessage(err.message);
    }
  };
  return (
    <div style={styles.container}>
      {/* HEADER */}
      <div style={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
          <img src={"/logo.png"} alt="Logo" style={styles.logo} />
          <h2 style={{ margin: 0 }}>Import Dashboard</h2>
        </div>

        <button onClick={handleLogout} style={styles.logoutButton}>
          Logout
        </button>
      </div>

      {/* UPLOAD */}
      <div style={styles.card}>
        <h3>Import CSV</h3>

        {message && <p style={styles.message}>{message}</p>}

        <form onSubmit={handleUpload} style={styles.formRow}>
          <select
            value={state}
            onChange={(e) => setState(e.target.value)}
            style={styles.select}
          >
            <option value="">Select State</option>
            {statesList.map((st) => (
              <option key={st} value={st}>
                {st}
              </option>
            ))}
          </select>

          <input
            type="file"
            accept=".csv"
            ref={fileInputRef}
            onChange={(e) => setFile(e.target.files[0])}
          />

          <button type="submit" style={styles.button} disabled={loading}>
            {loading ? "Uploading..." : "Upload CSV"}
          </button>
        </form>
      </div>

      {/* DATA */}
      <div style={styles.card}>
        <h3>Imported Data</h3>

        <div style={{ marginBottom: "10px" }}>
          <select
            value={location}
            onChange={(e) => {
              const val = e.target.value;
              setLocation(val);
              localStorage.setItem("location", val);
            }}
            style={styles.select}
          >
            <option value="CA">CA</option>
            <option value="WA">WA</option>
            <option value="NJ">NJ</option>
          </select>
        </div>
        {/* GLOBAL LOADER */}
        {dataLoading ? (
          <div style={styles.loaderWrapper}>
            <div style={styles.loader}></div>
            <p>Loading data...</p>
          </div>
        ) : (
          <>
            {/* COPIERS */}
            <h4>Copiers / Wide Format</h4>
            {Object.keys(copiers).length === 0 ? (
              <p style={styles.empty}>No copier data available</p>
            ) : (
              <div style={styles.tableWrapper}>
                <table style={styles.table}>
                  <thead style={styles.thead}>
                    <tr>
                      {[
                        "SKU",
                        "Model",
                        "Manufacturer",
                        "TTL Meter",
                        "CLR Meter",
                        "B/W Meter",
                        "Description",
                        "Serial",
                        "Pass",
                        "Action",
                      ].map((h) => (
                        <th key={h} style={styles.th}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(copiers).map(([manufacturer, items]) => (
                      <React.Fragment key={manufacturer}>
                        <tr>
                          <td colSpan="10" style={styles.groupHeader}>
                            {manufacturer}
                          </td>
                        </tr>
                        {items.map((row, i) => (
                          <tr
                            key={i}
                            style={i % 2 === 0 ? styles.rowEven : styles.rowOdd}
                          >
                            <td style={styles.td}>{row.sku}</td>
                            <td style={styles.td}>{row.model}</td>
                            <td style={styles.td}>{row.manufacturer}</td>
                            <td style={styles.td}>{row.totalMeter}</td>
                            <td style={styles.td}>{row.colorMeter}</td>
                            <td style={styles.td}>{row.bwMeter}</td>
                            <td style={styles.td}>{row.description}</td>
                            <td style={styles.td}>{row.serialNumber}</td>
                            <td style={styles.td}>{row.passCopy}</td>
                            <td style={styles.td}>
                              <button
                                style={styles.deleteBtn}
                                onClick={() => handleDelete(row.id)}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* PRINTERS */}
            <h4 style={{ marginTop: "20px" }}>Printers</h4>
            {Object.keys(printers).length === 0 ? (
              <p style={styles.empty}>No printer data available</p>
            ) : (
              <div style={styles.tableWrapper}>
                <table style={styles.table}>
                  <thead style={styles.thead}>
                    <tr>
                      {[
                        "SKU",
                        "Model",
                        "Manufacturer",
                        "TTL Meter",
                        "CLR Meter",
                        "B/W Meter",
                        "Description",
                        "Serial",
                        "Pass",
                        "Action",
                      ].map((h) => (
                        <th key={h} style={styles.th}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(printers).map(([manufacturer, items]) => (
                      <React.Fragment key={manufacturer}>
                        <tr>
                          <td colSpan="10" style={styles.groupHeader}>
                            {manufacturer}
                          </td>
                        </tr>
                        {items.map((row, i) => (
                          <tr
                            key={i}
                            style={i % 2 === 0 ? styles.rowEven : styles.rowOdd}
                          >
                            <td style={styles.td}>{row.sku}</td>
                            <td style={styles.td}>{row.model}</td>
                            <td style={styles.td}>{row.manufacturer}</td>
                            <td style={styles.td}>{row.totalMeter}</td>
                            <td style={styles.td}>{row.colorMeter}</td>
                            <td style={styles.td}>{row.bwMeter}</td>
                            <td style={styles.td}>{row.description}</td>
                            <td style={styles.td}>{row.serialNumber}</td>
                            <td style={styles.td}>{row.passCopy}</td>
                            <td style={styles.td}>
                              <button
                                style={styles.deleteBtn}
                                onClick={() => handleDelete(row.id)}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Import;

const styles = {
  container: {
    padding: "30px",
    background: "#eef2f7",
    minHeight: "100vh",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  logo: { width: "50px" },
  logoutButton: {
    padding: "8px 16px",
    background: "#dc3545",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
  card: {
    background: "#fff",
    padding: "20px",
    borderRadius: "12px",
    marginBottom: "20px",
    boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
  },
  formRow: { display: "flex", gap: "10px" },
  select: { padding: "8px", borderRadius: "6px" },
  button: {
    background: "#28a745",
    color: "#fff",
    padding: "8px 16px",
    border: "none",
    borderRadius: "6px",
  },
  message: { color: "green" },
  empty: { textAlign: "center", padding: "20px", color: "#888" },

  tableWrapper: { overflowX: "auto" },

  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: "0",
    marginTop: "10px",
    fontSize: "14px",
    borderRadius: "10px",
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
  },

  thead: {
    background: "#1f2937",
    color: "#fff",
  },

  th: {
    padding: "12px",
    textAlign: "left",
    fontWeight: "600",
  },

  td: {
    padding: "10px",
    borderBottom: "1px solid #eee",
  },

  rowEven: { background: "#fff" },
  rowOdd: { background: "#f9fafb" },

  groupHeader: {
    fontWeight: "bold",
    background: "#e5e7eb",
    padding: "10px",
  },
  deleteBtn: {
    background: "#ef4444",
    color: "#fff",
    border: "none",
    padding: "6px 10px",
    borderRadius: "5px",
    cursor: "pointer",
    fontSize: "12px",
  },
  loaderWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "30px",
  },

  loader: {
    width: "40px",
    height: "40px",
    border: "4px solid #e5e7eb",
    borderTop: "4px solid #2563eb",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
};
